export type Screen = 'LANDING' | 'VENT' | 'LISTEN' | 'UNIVERSE' | 'MATCHING' | 'DASHBOARD' | 'LETGO';

export interface Message {
  id: string;
  text?: string;
  type: 'text' | 'image' | 'audio';
  url?: string;
  // Voice notes: the length in seconds, measured by the sender.
  duration?: number;
  sender: 'user' | 'other';
  authorName?: string;
  timestamp?: number;
  // Gentle support cards shown only on this device: to the person who wrote
  // something heavy (care-self) or to their chat partner (care-listener).
  // 'feeling' is the "How did you feel?" card after a chat ends.
  // 'meet-intro' is the one-time Meet Again tip in a person's first chat.
  // 'nudge' is the small chip under an AI reply that mentioned Let Go, the Sky or Chat Hour.
  kind?: 'care-self' | 'care-listener' | 'feeling' | 'meet-intro' | 'nudge';
  // For kind 'nudge': which part of Zuzu the AI mentioned.
  nudge?: 'letgo' | 'sky' | 'bye';
  // For kind 'feeling' and 'meet-intro': the chat it is about.
  roomId?: string;
  // Reactions on this message: one from each person at most.
  reactions?: { mine?: string; theirs?: string };
  // Your own message on its way: still sending (e.g. while reconnecting), or it
  // could not be delivered before the chat ended. No status means it arrived.
  status?: 'sending' | 'failed';
  // A reply quotes one earlier message (a small copy, so it shows even if the
  // original is far up). `mine` is from this person's point of view.
  replyTo?: ReplyQuote;
  // The sender changed the text after sending (text messages, within 15 minutes).
  edited?: boolean;
  // Your photo was opened by the other person ("Tap to view"). Only ever set to
  // true: Zuzu never shows that a photo was not opened. Nothing is stored.
  viewed?: boolean;
}

export interface ReplyQuote {
  id: string;
  text?: string;
  type: 'text' | 'image' | 'audio';
  mine: boolean;
}

export interface Thought {
  id: string;
  text: string;
  x: number;
  y: number;
  size: number;
  emoji?: string;
  isNew?: boolean;
  replies?: ThoughtReply[]; // approved replies only
  feelings?: string[]; // what it is about, main one first (src/content/skyFeelings.ts)
  meToo?: number; // how many people said "Me too"
  pending?: boolean; // my own thought, waiting for a check (only I see it)
}

export interface ThoughtReply {
  id: string;
  text: string;
  name: string;
  submittedAt: string;
  loved?: boolean; // the thought's writer loved this reply
}

export const ADJECTIVES = ["Serene", "Quiet", "Gentle", "Still", "Soft", "Hushed", "Tender", "Tranquil", "Golden", "Luminous", "Amber", "Radiant", "Glowing", "Warm", "Pale", "Silver", "Misty", "Wandering", "Ancient", "Drifting", "Rising", "Hopeful", "Wistful", "Dreaming", "Timeless", "Velvet", "Ivory", "Cobalt", "Twilight", "Mossy", "Frosted", "Blooming", "Peaceful", "Graceful", "Flowing", "Bright", "Caring", "Healing", "Celestial", "Boundless", "Eternal", "Sacred", "Fearless", "Infinite", "Breezy", "Dewy", "Sunlit", "Starlit", "Melting", "Honeyed"];
export const ELEMENTS = ["Petal", "Leaf", "Seed", "Fern", "Moss", "Reed", "Birch", "Willow", "River", "Tide", "Mist", "Rain", "Brook", "Wave", "Shore", "Dew", "Star", "Cloud", "Moon", "Dawn", "Dusk", "Glow", "Beam", "Spark", "Aurora", "Stone", "Frost", "Glacier", "Flame", "Breeze", "Lantern", "Echo", "Candle", "Garden", "Meadow", "Haven", "Horizon", "Sanctuary", "Lumen", "Crest", "Vale", "Drift", "Caramel", "Cocoa", "Truffle", "Phoenix", "Unicorn", "Panda", "Koala", "Jalebi"];

// Short prompts above the chat box. Each chat shows a few, picked at random,
// so they feel fresh. Keep them short and in Zuzu's warm, plain voice.
// Reactions on a single chat message. The server accepts only these.
export const REACTIONS = ['❤️', '😂', '🫶', '😢', '🙏', '👍'] as const;

// A message id both people in the chat share (reactions point to it).
export const newMessageId = (): string =>
  crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const STAR_COLORS = ['#ffffff'];
export const THOUGHT_COLORS = ['#ffffff', '#3797F0', '#9B51E0', '#F2C94C', '#EB5757', '#27AE60', '#F2994A'];
